import { Upload } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import { useObsWizard } from "@/features/obs/hooks/obs-wizard-context";
import { chartsComplete } from "@/features/obs/lib/chart-labels";
import { STEPS, stepIndex } from "@/features/obs/lib/wizard-steps";

/**
 * The action bar. Every forward move belongs to a step; the back move belongs to
 * the wizard.
 */
export function ObsWizardFooter() {
  const {
    step,
    goTo,
    gates,
    draft,
    slugCheck,
    unitsDone,
    busy,
    saving,
    published,
    needsWorkbookAgain,
    createAndUpload,
    saveCharts,
    publish,
    requestExit,
  } = useObsWizard();

  const index = stepIndex(step);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
      <Button variant="ghost" onClick={requestExit} disabled={busy}>
        {published ? "إغلاق" : "إلغاء"}
      </Button>

      <div className="flex gap-2">
        {/*
          Available on every step but the first, and it STAYS available after the
          report has been created. It used to vanish the moment the workbook
          applied, which made the last two steps one-way: an operator who spotted
          a wrong unit or a mis-mapped column on the preview had no route back and
          had to abandon the report and walk the wizard again.

          Safe because everything the earlier steps collect is patchable. The
          units step PATCHES instead of creating on a second pass, and re-uploads
          only when something the parse depends on actually changed. The slug is
          the one field that cannot move, so it locks itself rather than
          pretending otherwise.

          Hidden once published — past that the wizard is a receipt.
        */}
        {step !== "identity" && !published && (
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => goTo(STEPS[Math.max(0, index - 1)].id)}
          >
            السابق
          </Button>
        )}

        {/*
          Waits on the slug check as well as on the fields. A slug that is
          already taken fails `identityDone`; while the report list is still
          loading nothing here knows yet, and moving on would only surface the
          collision three steps later at the create call — which is the whole
          thing this check exists to prevent.

          Only `checking` waits. A check that FAILED lets the operator through
          with a warning: the create call still refuses a duplicate, and a
          broken list must not be able to wedge step one.
        */}
        {step === "identity" && (
          <Button
            disabled={!gates.identityDone || slugCheck === "checking"}
            onClick={() => goTo("file")}
          >
            التالي
          </Button>
        )}

        {/*
          The file step normally advances ITSELF — picking a workbook profiles it
          and moves on, so it never needed a button. Stepping BACK to it does not
          re-pick a file, which left the step with no way forward: the operator had
          to re-choose the same file just to escape. Shown only once a profile
          exists, i.e. only when there is something to go forward to.
        */}
        {step === "file" && gates.hasProfile && !busy && (
          <Button onClick={() => goTo("mapping")}>التالي</Button>
        )}

        {step === "mapping" && (
          <Button
            disabled={!gates.mappingDone || busy}
            onClick={() => goTo("units")}
          >
            التالي
          </Button>
        )}

        {step === "units" && (
          <Button
            disabled={!unitsDone || busy || needsWorkbookAgain}
            onClick={createAndUpload}
          >
            {busy ? <Loader /> : <Upload className="size-4" />}
            {/* On a second pass this saves an existing report rather than
                creating one, and says so — «إنشاء» on a report that already
                exists reads like it is about to make another. */}
            {draft.createdSlug ? "حفظ ومتابعة" : "إنشاء ورفع البيانات"}
          </Button>
        )}

        {step === "charts" && (
          <Button
            disabled={busy || !chartsComplete(draft.charts)}
            onClick={saveCharts}
          >
            {saving ? <Loader /> : null}
            التالي
          </Button>
        )}

        {step === "publish" && draft.uploaded?.applied && !published && (
          <Button disabled={busy} onClick={publish}>
            {saving ? <Loader /> : null}
            نشر
          </Button>
        )}
      </div>
    </div>
  );
}
